import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/useAuth';

// SMS entrants non lus (direction='in', read_at IS NULL). Sert aux points
// rouges du Répertoire et au badge du menu. Partagé via React Query.

export const SMS_UNREAD_KEY = ['sms-unread'] as const;

export interface UnreadSms {
  total: number;
  byCounterparty: Map<string, number>;
}

const EMPTY: UnreadSms = { total: 0, byCounterparty: new Map() };

export function useUnreadSms(): UnreadSms {
  const { isAdminOrOwner, hasPermission } = useAuth();
  const enabled = isAdminOrOwner || hasPermission('directory.view');

  const query = useQuery({
    queryKey: SMS_UNREAD_KEY,
    enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<UnreadSms> => {
      const { data, error } = await supabase
        .from('sms_messages')
        .select('counterparty')
        .eq('direction', 'in')
        .is('read_at', null);
      if (error) throw error;
      const byCounterparty = new Map<string, number>();
      for (const r of (data ?? []) as { counterparty: string }[]) {
        byCounterparty.set(
          r.counterparty,
          (byCounterparty.get(r.counterparty) ?? 0) + 1
        );
      }
      let total = 0;
      for (const v of byCounterparty.values()) total += v;
      return { total, byCounterparty };
    },
  });

  return query.data ?? EMPTY;
}

/** Marque tous les entrants d'un numéro comme lus (à l'ouverture de la convo). */
export function useMarkConversationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (counterparty: string): Promise<void> => {
      const { error } = await supabase
        .from('sms_messages')
        .update({ read_at: new Date().toISOString() })
        .eq('counterparty', counterparty)
        .eq('direction', 'in')
        .is('read_at', null);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SMS_UNREAD_KEY });
    },
  });
}
